import { ChevronRight, Layers, Search, Sparkle } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useDebounce } from 'hooks';
import { useGetSearchThreadsQuery } from 'modules/threads/threadsApi';
import { useCopilotSearchQuery } from 'modules/copilot/copilotApi';
import { Dialog, DialogContent } from 'components/ui/dialog';
import { cn } from 'lib/utils';
import { Link, useNavigate } from 'react-router-dom';

const Loader = () => {
  return (
    <div className="flex flex-row justify-center gap-2 p-4">
      <div className="h-2 w-2 animate-bounce rounded-full bg-secondary [animation-delay:-0.3s]" />
      <div className="h-2 w-2 animate-bounce rounded-full bg-secondary [animation-delay:-0.15s]" />
      <div className="h-2 w-2 animate-bounce rounded-full bg-secondary" />
    </div>
  );
};

interface CommandProps extends React.PropsWithChildren {
  selected: boolean;
  select: () => void;
}

const Command: React.FC<CommandProps> = ({ selected, select, children }) => {
  return (
    <div
      className={cn(
        'mx-1 flex flex-row items-center justify-between truncate rounded px-3 py-1',
        selected && 'bg-secondary'
      )}
      onMouseOver={select}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onFocus={() => {}}
    >
      {children}
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </div>
  );
};

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const CommandPalette: React.FC<Props> = ({ open, setOpen }) => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const debounced = useDebounce(search, 500);

  // Selected option
  const [selected, setSelected] = useState(0);

  const { data: threads, isFetching: loadingThreads } = useGetSearchThreadsQuery(
    {
      limit: 5,
      offset: 0,
      id: undefined,
      search: debounced,
    },
    { skip: debounced === '' }
  );
  const {
    data: results,
    isFetching: loadingResults,
    isUninitialized,
  } = useCopilotSearchQuery({ query: debounced }, { skip: debounced === '' });

  // Navigate to thread. TODO: If there's a file, add it to query params
  const handleSelect = () => {
    const threadsCount = threads?.results.length ?? 0;
    const isThreadResult = selected < threadsCount;

    if (isThreadResult) {
      const thread = threads?.results[selected];
      if (!thread) return;
      navigate(`/thread/${thread.id}`);
    } else {
      const result = results?.[selected - threadsCount];
      if (!result) return;
      navigate(`/thread/${result.metadata.threadUuid}`);
    }
    setOpen(false);
  };

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const resultsCount = (threads?.results.length ?? 0) + (results?.length ?? 0);
        setSelected((prev) => (prev === resultsCount - 1 ? 0 : prev + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const resultsCount = (threads?.results.length ?? 0) + (results?.length ?? 0);
        setSelected((prev) => (prev === 0 ? resultsCount - 1 : prev - 1));
      } else if (e.key === 'Enter') {
        handleSelect();
      }
    };

    if (open) {
      document.addEventListener('keydown', down);
    } else {
      document.removeEventListener('keydown', down);
    }
    return () => document.removeEventListener('keydown', down);
  }, [open, threads, results, selected]);

  // Clear search on open/close
  useEffect(() => {
    setSearch('');
  }, [open]);

  // Reset to top item on text change
  useEffect(() => setSelected(0), [debounced]);

  const showLoader = loadingResults || loadingThreads || isUninitialized;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="w-5/6 gap-0 p-0 md:w-full">
        <div className="flex items-center px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            className={`flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none
placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50`}
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {search !== '' && (
          <>
            <div className="mx-1 h-[1px] bg-secondary" />
            {showLoader ? (
              <Loader />
            ) : (
              <>
                <span className="flex flex-row items-center gap-1 p-2 text-xs text-muted-foreground">
                  <Layers className="h-3 w-3" /> Threads
                </span>
                {threads?.results.length === 0 && (
                  <span className="py-2 text-center text-sm text-muted-foreground">No Results</span>
                )}
                {threads?.results.map((thread, index) => (
                  <Link to={`/thread/${thread.id}`} onClick={() => setOpen(false)}>
                    <Command select={() => setSelected(index)} selected={selected === index}>
                      <span className="text-sm font-semibold">{thread.name}</span>
                    </Command>
                  </Link>
                ))}
                <div className="m-1 h-[1px] bg-secondary" />
                <span className="flex flex-row items-center gap-1 p-2 text-xs text-muted-foreground">
                  <Sparkle className="h-3 w-3" /> AI Results
                </span>
                {results?.map((result, index) => (
                  <Link to={`/thread/${result.metadata.threadUuid}`} onClick={() => setOpen(false)}>
                    <Command
                      select={() => setSelected(index + (threads?.results.length ?? 0))}
                      selected={selected === index + (threads?.results.length ?? 0)}
                    >
                      <div className="flex w-[95%] flex-col">
                        <span className="text-sm font-semibold">{result.metadata.name}</span>
                        <span className="line-clamp-3 whitespace-normal break-words pl-1 text-xs text-muted-foreground">
                          {result.pageContent}
                        </span>
                      </div>
                    </Command>
                  </Link>
                ))}
                {results?.length === 0 && (
                  <span className="py-2 text-center text-sm text-muted-foreground">No Results</span>
                )}
                <div className="h-2" />
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
